// Client-side "windowed paging" for high-volume time-series endpoints.
//
// The API caps every request to a maximum date-range window (env-configurable
// on the server, default 30 days, hard 6-month ceiling) and returns HTTP 400
// for anything larger. To fetch a longer span the client must split it into
// <= cap windows, fetch them (throttled, in parallel), and merge the results.
//
// The cap itself lives on the API — this module never hardcodes a business
// limit. It starts at the API's documented default (30) and self-corrects from
// `paging.maxRangeDays` on any successful response, or `maxRangeDays` on a 400
// body, so changing the server's MAX_QUERY_RANGE_DAYS needs no frontend change.
//
// All timestamps here are Unix **seconds**, matching the service layer.

import { compareFetchLimit } from './concurrencyLimit'
import { ApiClientError } from './axios'

const SECONDS_PER_DAY = 24 * 60 * 60
const DEFAULT_MAX_RANGE_DAYS = 30
const ABSOLUTE_MAX_RANGE_DAYS = 180 // mirrors the server's hard 6-month ceiling

let cachedMaxRangeDays = DEFAULT_MAX_RANGE_DAYS

export function getMaxRangeDays(): number {
  return cachedMaxRangeDays
}

function sanitizeCap(value: unknown): number | undefined {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 1) return undefined
  return Math.min(Math.floor(n), ABSOLUTE_MAX_RANGE_DAYS)
}

/** Learn the cap from a successful response body's `paging.maxRangeDays`. */
export function noteMaxRangeDaysFromResponse(body: any): void {
  const cap = sanitizeCap(body?.paging?.maxRangeDays)
  if (cap) cachedMaxRangeDays = cap
}

/** Learn the cap from a 400 rejection body (`{ error, maxRangeDays }`). */
function noteMaxRangeDaysFromError(err: unknown): number | undefined {
  const details = (err as ApiClientError | undefined)?.error?.details as any
  const cap = sanitizeCap(details?.maxRangeDays)
  if (cap) cachedMaxRangeDays = cap
  return cap
}

function isRangeTooLarge(err: unknown): boolean {
  return err instanceof ApiClientError && err.statusCode === 400
}

export function spanExceedsCap(from: number, to: number, capDays: number = cachedMaxRangeDays): boolean {
  return to - from > Math.max(1, Math.floor(capDays)) * SECONDS_PER_DAY
}

/**
 * Split [from, to] into windows each spanning <= capDays. Windows are returned
 * most-recent first and tile the range without overlap (each older window ends
 * one second before the previous one begins). A range already within the cap
 * yields a single window.
 */
export function splitIntoWindows(
  from: number,
  to: number,
  capDays: number = cachedMaxRangeDays
): Array<{ from: number; to: number }> {
  if (!(to > from)) return [{ from, to }]
  const capSec = Math.max(1, Math.floor(capDays)) * SECONDS_PER_DAY
  if (to - from <= capSec) return [{ from, to }]

  const windows: Array<{ from: number; to: number }> = []
  let windowTo = to
  while (windowTo > from) {
    const windowFrom = Math.max(from, windowTo - capSec)
    windows.push({ from: windowFrom, to: windowTo })
    windowTo = windowFrom - 1
  }
  return windows
}

/**
 * Fetch a [from, to] range as one or more <= cap windows, throttled through the
 * shared compareFetchLimit, then merge the parts back into a single result of
 * the same shape the caller expects. Self-heals if a window is still rejected
 * with 400 (server cap smaller than assumed) by re-splitting with the learned cap.
 */
export async function fetchWindowedRange<T>(
  from: number,
  to: number,
  fetchWindow: (from: number, to: number) => Promise<T>,
  merge: (parts: T[]) => T
): Promise<T> {
  const runWindow = async (wFrom: number, wTo: number): Promise<T> => {
    try {
      const res = await compareFetchLimit(() => fetchWindow(wFrom, wTo))
      noteMaxRangeDaysFromResponse(res)
      return res
    } catch (err) {
      if (isRangeTooLarge(err)) {
        const learnedCap = noteMaxRangeDaysFromError(err)
        if (learnedCap && spanExceedsCap(wFrom, wTo, learnedCap)) {
          const subs = splitIntoWindows(wFrom, wTo, learnedCap)
          if (subs.length > 1) {
            const parts = await Promise.all(subs.map((s) => runWindow(s.from, s.to)))
            return merge(parts)
          }
        }
      }
      throw err
    }
  }

  const windows = splitIntoWindows(from, to)
  if (windows.length <= 1) return runWindow(from, to)
  const parts = await Promise.all(windows.map((w) => runWindow(w.from, w.to)))
  return merge(parts)
}

// --- Progressive (incremental-render) windowing -------------------------------
//
// The windows above exist to satisfy the server's range cap: as few requests as
// possible, all awaited before anything is returned. Charts want the opposite —
// smaller slices that land one at a time so the page can draw the first of them
// while the rest are still in flight.

const MIN_PROGRESSIVE_WINDOW_DAYS = 7
const MAX_PROGRESSIVE_WINDOWS = 12

/** Window size for a range: ~7 days, growing so a long range stays ~12 requests. */
export function progressiveWindowDays(from: number, to: number): number {
  const totalDays = Math.max(1, Math.ceil((to - from) / SECONDS_PER_DAY))
  const sized = Math.max(MIN_PROGRESSIVE_WINDOW_DAYS, Math.ceil(totalDays / MAX_PROGRESSIVE_WINDOWS))
  return Math.min(sized, cachedMaxRangeDays)
}

/**
 * Split [from, to] into render-sized windows, most-recent first.
 *
 * Interior boundaries are snapped to multiples of the window size since the
 * epoch, so stepping the date range forward or back reuses the windows already
 * cached instead of re-fetching a shifted set. Only the newest window (which
 * ends at the caller's `to`) and the oldest (clamped to `from`) are ragged.
 */
export function splitIntoProgressiveWindows(
  from: number,
  to: number,
  windowDays: number = progressiveWindowDays(from, to)
): Array<{ from: number; to: number }> {
  if (!(to > from)) return [{ from, to }]

  const windowSec = Math.max(1, Math.floor(windowDays)) * SECONDS_PER_DAY
  // A range that already fits stays one request, even when a boundary cuts through it.
  if (to - from <= windowSec) return [{ from, to }]

  const windows: Array<{ from: number; to: number }> = []
  let windowTo = to

  while (windowTo > from) {
    const boundary = Math.floor(windowTo / windowSec) * windowSec
    const windowFrom = Math.max(from, boundary === windowTo ? windowTo - windowSec : boundary)
    if (windowFrom >= windowTo) break
    windows.push({ from: windowFrom, to: windowTo })
    windowTo = windowFrom - 1
  }

  return windows.length > 0 ? windows : [{ from, to }]
}
